"use client";

import { Suspense, useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import {
  identifyPostHogUser,
  initPostHog,
  posthog,
  resetPostHogUser,
} from "@/lib/posthog";
import { supabase } from "@/lib/supabase";
import { api } from "@/lib/api";

function PageviewTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!pathname) return;
    const query = searchParams?.toString();
    const url = query ? `${pathname}?${query}` : pathname;
    posthog.capture("$pageview", {
      $current_url: window.location.origin + url,
    });
  }, [pathname, searchParams]);

  return null;
}

function AuthIdentityTracker() {
  useEffect(() => {
    let mounted = true;

    const enrichAndIdentify = async (
      userId: string,
      email: string | null | undefined,
    ) => {
      // Identify with what we have synchronously, then enrich with backend
      // profile data once it lands. This avoids a race where the first event
      // fires before /user/me resolves.
      identifyPostHogUser(userId, {
        email: email ?? undefined,
      });

      try {
        const profile = await api.getMe();
        if (!mounted) return;
        identifyPostHogUser(userId, {
          email: profile.email ?? email ?? undefined,
          name: profile.name ?? undefined,
          locale: profile.locale ?? undefined,
          country: profile.country ?? undefined,
          familyRole: profile.familyRole ?? undefined,
          customName: profile.customName ?? undefined,
          onboardingCompleted: profile.onboardingCompleted ?? undefined,
        });
      } catch {
        // Silent — identify with email-only is good enough
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted || !session?.user) return;
      enrichAndIdentify(session.user.id, session.user.email);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_OUT") {
        resetPostHogUser();
        return;
      }
      if (!session?.user) return;
      enrichAndIdentify(session.user.id, session.user.email);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  return null;
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    initPostHog();
  }, []);

  return (
    <PHProvider client={posthog}>
      <Suspense fallback={null}>
        <PageviewTracker />
        <AuthIdentityTracker />
      </Suspense>
      {children}
    </PHProvider>
  );
}
